import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Camera, Check, Loader, User, Key, Shield, Users, ChevronRight, ChevronDown, Book, ExternalLink, Mail } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { useApp } from '../AppProvider';
import { GoogleAccountSection } from './GoogleAccountSection';
import { MyCalendarSection } from './calendar/MyCalendarSection';
import DashboardVersionChoice from '../dashboard-home/switch/DashboardVersionChoice';
import { NotionDrawer } from '../ui/notion/NotionDrawer';
import { usePermissionsStore } from '../../lib/stores/permissionsStore';

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Resource names in Italian
const RESOURCE_NAMES: Record<string, string> = {
  clients: 'Clienti',
  deals: 'Offerte',
  leads: 'Lead',
  lead_notes: 'Note Lead',
  lead_sources: 'Fonti Lead',
  dashboard: 'Dashboard',
  calendar: 'Calendario',
  fattureincloud: 'Fatture in Cloud',
  contracts: 'Contratti',
  user_commissions: 'Commissioni Utenti',
  commissions: 'Commissioni',
  commission_groups: 'Gruppi Commissioni',
  commission_reports: 'Report Commissioni',
  commission_advanced: 'Commissioni Avanzate',
  whatsapp: 'WhatsApp',
  documents: 'Documenti',
  newsletter: 'Newsletter',
  email_signature: 'Firma Email',
  inventory: 'Magazzino',
  rentals: 'Noleggi',
  projects: 'Progetti',
  tasks: 'Task',
  tickets: 'Ticket',
  analytics: 'Analytics',
  reports: 'Report',
  services: 'Servizi',
  service_costs: 'Costi Servizi',
  service_margins: 'Margini Servizi',
  client_expenses: 'Spese Cliente',
  service_task_templates: 'Template Task',
  external_partners: 'Partner Esterni',
  partner_tasks: 'Task Partner',
  users: 'Utenti',
  roles: 'Ruoli',
  settings: 'Impostazioni',
  api_config: 'Configurazioni API',
  categories: 'Categorie',
  templates: 'Template',
  signatures: 'Firme',
  payment_methods: 'Metodi Pagamento',
  referrers: 'Referenti',
  custom_fields: 'Campi Personalizzati',
  custom_queries: 'Query Personalizzate',
  invoices: 'Fatture'
};

// Permission action names
const ACTION_NAMES: Record<string, string> = {
  view: 'Visualizza',
  create: 'Crea',
  update: 'Modifica',
  delete: 'Elimina',
  manage: 'Gestisci',
  assign: 'Assegna',
  filter_by_user: 'Filtra per utente',
  generate: 'Genera',
  sync: 'Sincronizza',
  configure: 'Configura',
  refresh: 'Aggiorna',
  export: 'Esporta',
  send: 'Invia',
  verify: 'Verifica',
  convert: 'Converti',
  complete: 'Completa'
};

export function ProfileEditModal({ isOpen, onClose }: ProfileEditModalProps) {
  const { user, updateProfile } = useAuth();
  const { toast } = useApp();
  const { permissions } = usePermissionsStore();

  // Profile data
  const [profileImageUrl, setProfileImageUrl] = useState(user?.user_metadata?.profile_image_url || '');
  const [localFirstName, setLocalFirstName] = useState('');
  const [localLastName, setLocalLastName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Role and manager
  const [userRole, setUserRole] = useState<{ name: string; description: string } | null>(null);
  const [manager, setManager] = useState<{ full_name: string; email: string } | null>(null);
  const [loadingRole, setLoadingRole] = useState(false);

  // Expanded permission groups
  const [expandedResources, setExpandedResources] = useState<Set<string>>(new Set());

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen && user) {
      const fullName = user.user_metadata?.full_name || '';
      const nameParts = fullName.trim().split(' ');
      setLocalFirstName(nameParts[0] || '');
      setLocalLastName(nameParts.slice(1).join(' ') || '');
      setProfileImageUrl(user.user_metadata?.profile_image_url || '');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);

      // Fetch role and manager
      fetchRoleAndManager();
    }
  }, [isOpen, user]);

  // Fetch user role and manager
  const fetchRoleAndManager = async () => {
    if (!user?.id) return;
    setLoadingRole(true);

    try {
      // Fetch role
      const { data: roleData } = await supabase
        .from('user_roles')
        .select(`
          role:roles (
            id,
            name,
            description
          )
        `)
        .eq('user_id', user.id)
        .single();

      if (roleData?.role) {
        setUserRole(roleData.role as { name: string; description: string });
      }

      // Fetch manager
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('manager_id')
        .eq('id', user.id)
        .single();

      if (profileData?.manager_id) {
        const { data: managerData } = await supabase
          .from('user_profiles')
          .select('full_name, email')
          .eq('id', profileData.manager_id)
          .single();

        if (managerData) {
          setManager(managerData);
        }
      } else {
        setManager(null);
      }
    } catch (error) {
      console.error('Error fetching role/manager:', error);
    } finally {
      setLoadingRole(false);
    }
  };

  // Group permissions by resource
  const permissionsByResource = useMemo(() => {
    const grouped: Record<string, typeof permissions> = {};
    permissions.forEach(p => {
      if (!grouped[p.resource]) grouped[p.resource] = [];
      grouped[p.resource].push(p);
    });
    return grouped;
  }, [permissions]);

  // Handle name blur - save on blur
  const handleNameBlur = async () => {
    const newFullName = `${localFirstName} ${localLastName}`.trim();
    const currentFullName = user?.user_metadata?.full_name || '';

    if (newFullName && newFullName !== currentFullName) {
      setSavingName(true);
      try {
        await updateProfile({
          full_name: newFullName,
          profile_image_url: profileImageUrl
        });
        toast.success('Nome aggiornato');
      } catch (error) {
        console.error('Error updating name:', error);
        toast.error('Errore durante l\'aggiornamento');
        // Revert
        const nameParts = currentFullName.trim().split(' ');
        setLocalFirstName(nameParts[0] || '');
        setLocalLastName(nameParts.slice(1).join(' ') || '');
      } finally {
        setSavingName(false);
      }
    }
  };

  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    setChangingPassword(true);

    try {
      // First verify old password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user!.email!,
        password: oldPassword
      });

      if (signInError) {
        toast.error('La password attuale non è corretta');
        setChangingPassword(false);
        return;
      }

      // Then update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        toast.error('Errore durante l\'aggiornamento della password');
      } else {
        toast.success('Password aggiornata con successo');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordForm(false);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error('Errore durante l\'aggiornamento della password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Handle profile image upload
  const handleImageUpload = async (file: File) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato file non supportato. Usa JPG o PNG.');
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Il file è troppo grande. Dimensione massima: 2MB');
      return;
    }

    setUploadingImage(true);

    try {
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `profile_${user!.id}_${Date.now()}.${fileExt}`;

      // Upload to Storage
      const { error: uploadError } = await supabase.storage
        .from('uploads')
        .upload(`profile/${fileName}`, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        toast.error('Impossibile caricare l\'immagine');
        console.error('Upload error:', uploadError);
        return;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('uploads')
        .getPublicUrl(`profile/${fileName}`);

      // Update profile
      setProfileImageUrl(publicUrl);
      await updateProfile({
        full_name: `${localFirstName} ${localLastName}`.trim(),
        profile_image_url: publicUrl
      });

      toast.success('Immagine caricata con successo');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Errore durante il caricamento dell\'immagine');
    } finally {
      setUploadingImage(false);
    }
  };

  // Toggle resource expansion
  const toggleResource = (resource: string) => {
    setExpandedResources(prev => {
      const next = new Set(prev);
      if (next.has(resource)) {
        next.delete(resource);
      } else {
        next.add(resource);
      }
      return next;
    });
  };

  // Pannello laterale (regola UI: drawer, mai modali centrali). Una colonna sola: anagrafica,
  // password, ruolo e manager, poi Google, calendario e preferenze.
  const footer = (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
      <a
        href="https://flusia.it/it/wiki/"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 text-xs text-[#787774] dark:text-gray-400 hover:text-[#2383E2] transition-colors"
      >
        <Book className="h-3.5 w-3.5" />
        Guida e documentazione
        <ExternalLink className="h-3 w-3" />
      </a>
      <a
        href="mailto:hello@obly.it"
        className="flex items-center gap-1.5 text-xs text-[#787774] dark:text-gray-400 hover:text-[#2383E2] transition-colors"
      >
        <Mail className="h-3.5 w-3.5" />
        Assistenza: hello@obly.it
      </a>
    </div>
  );

  return (
    <NotionDrawer isOpen={isOpen} onClose={onClose} title="Il mio profilo" width="xl" footer={footer}>
            <div>
              {/* Profile Image */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative group">
                  <div className="h-24 w-24 rounded-full overflow-hidden border-2 border-[rgba(55,53,47,0.09)] dark:border-gray-700 flex items-center justify-center bg-[#FBFBFA] dark:bg-gray-800">
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <User className="h-10 w-10 text-[#787774] dark:text-gray-400" />
                    )}
                  </div>

                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="absolute bottom-0 right-0 bg-[#37352F] dark:bg-gray-700 text-white rounded-full p-2 hover:bg-[#37352F]/80 dark:hover:bg-gray-600 transition-colors"
                  >
                    {uploadingImage ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                  </button>

                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/jpeg,image/png"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleImageUpload(e.target.files[0]);
                      }
                    }}
                  />
                </div>
              </div>

              {/* Name Fields - Inline editable */}
              <div className="space-y-3 mb-6">
                <div className="space-y-1">
                  <label className="block text-xs text-[#787774] dark:text-gray-400 px-2">Nome</label>
                  <div className="flex items-center gap-2 px-2 py-1.5 rounded transition-colors hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.05]">
                    <User className="h-4 w-4 text-[#787774] dark:text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={localFirstName}
                      onChange={(e) => setLocalFirstName(e.target.value)}
                      onBlur={handleNameBlur}
                      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                      className="flex-1 bg-transparent text-sm text-[#37352F] dark:text-white focus:outline-none placeholder:text-[#787774]"
                      placeholder="Nome"
                    />
                    {savingName && <Loader className="h-3 w-3 animate-spin text-[#787774]" />}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs text-[#787774] dark:text-gray-400 px-2">Cognome</label>
                  <div className="flex items-center gap-2 px-2 py-1.5 rounded transition-colors hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.05]">
                    <User className="h-4 w-4 text-[#787774] dark:text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={localLastName}
                      onChange={(e) => setLocalLastName(e.target.value)}
                      onBlur={handleNameBlur}
                      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                      className="flex-1 bg-transparent text-sm text-[#37352F] dark:text-white focus:outline-none placeholder:text-[#787774]"
                      placeholder="Cognome"
                    />
                  </div>
                </div>
              </div>

              {/* Password Change Button/Form */}
              <div className="mb-6">
                {!showPasswordForm ? (
                  <button
                    onClick={() => setShowPasswordForm(true)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.05] rounded transition-colors"
                  >
                    <Key className="h-4 w-4 text-[#787774] dark:text-gray-400" />
                    Cambia password
                    <ChevronRight className="h-4 w-4 ml-auto text-[#787774] dark:text-gray-400" />
                  </button>
                ) : (
                  <div className="space-y-3 p-3 bg-[#FBFBFA] dark:bg-gray-800/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider">Cambia password</span>
                      <button
                        onClick={() => setShowPasswordForm(false)}
                        className="text-[#787774] hover:text-[#37352F] dark:hover:text-white"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <form onSubmit={handlePasswordChange} className="space-y-2">
                      <input
                        type="password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.16)] dark:border-gray-600 rounded text-[#37352F] dark:text-white focus:outline-none focus:border-[#2383E2]"
                        placeholder="Password attuale"
                        required
                      />
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.16)] dark:border-gray-600 rounded text-[#37352F] dark:text-white focus:outline-none focus:border-[#2383E2]"
                        placeholder="Nuova password"
                        minLength={6}
                        required
                      />
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.16)] dark:border-gray-600 rounded text-[#37352F] dark:text-white focus:outline-none focus:border-[#2383E2]"
                        placeholder="Conferma password"
                        minLength={6}
                        required
                      />
                      <button
                        type="submit"
                        disabled={changingPassword || !oldPassword || !newPassword || !confirmPassword}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-[#37352F] dark:bg-gray-700 rounded hover:bg-[#37352F]/80 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                      >
                        {changingPassword ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin" />
                            Aggiornamento...
                          </>
                        ) : (
                          'Aggiorna password'
                        )}
                      </button>
                    </form>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 my-4" />

              {/* Role Section */}
              <div className="mb-4">
                <h4 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider mb-3 px-2">
                  Ruolo e Permessi
                </h4>

                {loadingRole ? (
                  <div className="flex items-center gap-2 px-2 py-2">
                    <Loader className="h-4 w-4 animate-spin text-[#787774]" />
                    <span className="text-sm text-[#787774]">Caricamento...</span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 px-2 py-2 mb-3">
                      <Shield className="h-4 w-4 text-[#2383E2]" />
                      <span className="text-sm font-medium text-[#37352F] dark:text-white">
                        {userRole?.name || 'Nessun ruolo assegnato'}
                      </span>
                    </div>

                    {/* Permissions grouped by resource */}
                    {Object.keys(permissionsByResource).length > 0 && (
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {Object.entries(permissionsByResource).map(([resource, perms]) => (
                          <div key={resource}>
                            <button
                              onClick={() => toggleResource(resource)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.05] rounded transition-colors"
                            >
                              <ChevronDown
                                className={`h-3 w-3 text-[#787774] transition-transform ${
                                  expandedResources.has(resource) ? '' : '-rotate-90'
                                }`}
                              />
                              <span className="text-xs font-medium text-[#787774] dark:text-gray-400">
                                {RESOURCE_NAMES[resource] || resource}
                              </span>
                              <span className="text-xs text-[#787774]/60 ml-auto">
                                {perms.length}
                              </span>
                            </button>

                            {expandedResources.has(resource) && (
                              <div className="ml-5 pl-2 border-l border-[rgba(55,53,47,0.09)] dark:border-gray-700">
                                {perms.map(p => (
                                  <div key={p.id} className="flex items-center gap-1.5 py-1 px-2">
                                    <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                                    <span className="text-xs text-[#37352F] dark:text-white">
                                      {ACTION_NAMES[p.action] || p.action}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 my-4" />

              {/* Manager Section */}
              <div>
                <h4 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider mb-3 px-2">
                  Manager
                </h4>

                <div className="flex items-center gap-2 px-2 py-2">
                  <Users className="h-4 w-4 text-[#787774] dark:text-gray-400" />
                  {manager ? (
                    <span className="text-sm text-[#37352F] dark:text-white">{manager.full_name}</span>
                  ) : (
                    <span className="text-sm text-[#787774] dark:text-gray-400 italic">Nessun manager assegnato</span>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-gray-700 my-4" />
            <div>
              <h4 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider mb-4">
                Accesso Google
              </h4>
              <GoogleAccountSection userId={user?.id} />
              <h4 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider mb-3 mt-6">
                Il mio calendario
              </h4>
              <MyCalendarSection />
              <h4 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wider mb-3 mt-6">
                Preferenze
              </h4>
              <DashboardVersionChoice />
            </div>
    </NotionDrawer>
  );
}
